import { useState, useCallback } from 'react';
import { resolveCompanyIds, applyFilters, flattenRow } from './exportUtils';

function runWorker(payload) {
  return new Promise((resolve, reject) => {
    const worker = new Worker(new URL('./export.worker.js', import.meta.url));
    worker.onmessage = (e) => {
      resolve(e.data.buffer);
      worker.terminate();
    };
    worker.onerror = (err) => {
      reject(err);
      worker.terminate();
    };
    worker.postMessage(payload);
  });
}

function triggerBrowserDownload(buffer, filename) {
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default function useExport({ config, data, companyData }) {
  const [loading, setLoading] = useState(false);

  const triggerExport = useCallback(async (activeFilters) => {
    if (!data) return;
    setLoading(true);
    try {
      const filteredData = applyFilters(data, activeFilters, config.filters);
      const resolvedData = resolveCompanyIds(filteredData, companyData, config.companyFields);

      const date = new Date().toISOString().slice(0, 10);
      const filename = `${config.filename}-${date}.xlsx`;

      let sheets;

      if (config.fetchExtraSheets) {
        const extraSheets = await config.fetchExtraSheets(filteredData);
        const primaryRows = resolvedData.map(row => flattenRow(row, config.columns));
        const primaryHeaders = config.columns.map(c => c.header);
        sheets = [
          { name: config.sheetName || 'Data', headers: primaryHeaders, rows: primaryRows },
          ...extraSheets,
        ];
      } else {
        const rows = resolvedData.map(row => flattenRow(row, config.columns));
        const headers = config.columns.map(c => c.header);
        sheets = [{ name: config.sheetName || 'Data', headers, rows }];
      }

      const buffer = await runWorker({ sheets, filename });
      triggerBrowserDownload(buffer, filename);
    } finally {
      setLoading(false);
    }
  }, [config, data, companyData]);

  return { triggerExport, loading };
}
